import type { CSSProperties, FC } from 'react';
import { useId } from 'react';

import { getCssVariable } from 'shared/helpers/getCssVariable';

type ColorValue = CSSProperties['color'];

type SvgBgIconProps = {
  /**
   * Default widths on the components exported should be according to design mocks
   */
  width?: number;

  /**
   * Default heights on the components exported should be according to design mocks
   */
  height?: number;

  /**
   * Fill color; pass a CSS color value, e.g. `getCssVariable('od-content-primary')`.
   */
  color?: ColorValue;

  /**
   * Background color; pass a CSS color value.
   */
  bgColor?: ColorValue;
};

type SvgIconProps = Omit<SvgBgIconProps, 'bgColor'>;

const getColorValue = (color: ColorValue, fallback: ColorValue): ColorValue => color ?? fallback;

type Dimensions = { w: number; h: number };

/**
 * Calculates an appropriate width and height based on input.
 *  - if both width and height are supplied, those are used
 *  - if neither width nor height are supplied, originals are used
 *  - if either width or height are supplied, the other is calculated
 *    using the original values to maintain the aspect ratio
 */
const calcDimensions = (
  width: number | undefined,
  height: number | undefined,
  originalWidth: number,
  originalHeight: number,
): Dimensions => {
  if (width != null && height != null) {
    return { w: width, h: height };
  }

  const aspectRatio = originalWidth / originalHeight;

  if (width != null) {
    return { w: width, h: width / aspectRatio };
  }

  if (height != null) {
    return { w: height * aspectRatio, h: height };
  }

  return { w: originalWidth, h: originalHeight };
};

export const BigO = () => {
  const gradientId = useId();

  return (
    <svg
      width="222"
      height="326"
      viewBox="0 0 236 326"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        opacity="0.2"
        fillRule="evenodd"
        clipRule="evenodd"
        d="M195.205 115.978C195.205 16.4745 179.165 -24.7714 135.067 -24.7714C96.1767 -24.7714 76.5319 4.14247 76.5319 104.499C76.5319 203.577 91.7673 244.399 136.67 244.399C176.361 244.399 195.205 215.908 195.205 115.978ZM0.359375 113.429C0.359375 1.59133 72.9247 -42.6328 137.472 -42.6328C206.429 -42.6328 271.376 1.16744 271.376 106.625C271.376 219.31 198.812 262.261 134.263 262.261C65.3078 262.261 0.359375 215.485 0.359375 113.429ZM257.224 352.232C250.643 342.974 225.298 307.311 225.298 307.311C222.92 303.965 218.845 301.945 214.472 301.945H57.2635C52.8904 301.945 48.8155 303.965 46.4375 307.311L14.5166 352.225C13.5039 353.649 12.9666 355.308 12.9666 357.003V363.859C12.9666 366.145 14.7133 367.998 16.8689 367.998H254.867C257.022 367.998 258.769 366.145 258.769 363.859V357.003C258.769 355.308 258.236 353.654 257.224 352.232Z"
        fill={`url(#${gradientId})`}
      />
      <defs>
        <linearGradient
          id={gradientId}
          x1="-14.8754"
          y1="-69.9185"
          x2="223.942"
          y2="320.909"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="white" stopOpacity="0.59" />
          <stop offset="1" stopColor="white" stopOpacity="0.37" />
        </linearGradient>
      </defs>
    </svg>
  );
};

export const OpendoorLogo = () => {
  return (
    <svg width="61" height="61" viewBox="0 0 61 61" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="30.957" cy="30.5" r="30" fill="#21201F" />
      <g>
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M36.1029 26.6329C36.1029 18.3941 34.6915 14.9789 30.8109 14.9789C27.3887 14.9789 25.66 17.373 25.66 25.6824C25.66 33.8861 27.0007 37.2661 30.952 37.2661C34.4447 37.2661 36.1029 34.907 36.1029 26.6329ZM18.957 26.4218C18.957 17.1617 25.3426 13.5 31.0226 13.5C37.0906 13.5 42.8058 17.1266 42.8058 25.8584C42.8058 35.1887 36.4204 38.745 30.7402 38.745C24.6723 38.745 18.957 34.8721 18.957 26.4218ZM41.5604 46.1946C40.9814 45.4281 38.7511 42.4751 38.7511 42.4751C38.5418 42.1981 38.1832 42.0309 37.7984 42.0309H23.9645C23.5796 42.0309 23.2211 42.1981 23.0118 42.4751L20.2028 46.194C20.1137 46.312 20.0664 46.4493 20.0664 46.5896V47.1573C20.0664 47.3466 20.2201 47.5 20.4098 47.5H41.353C41.5427 47.5 41.6964 47.3466 41.6964 47.1573V46.5896C41.6964 46.4493 41.6496 46.3123 41.5604 46.1946Z"
          fill="white"
        />
      </g>
    </svg>
  );
};

export const OpendoorDashboardLogo: FC<SvgIconProps> = ({ color, width, height }) => {
  const fill = getColorValue(color, getCssVariable('od-content-primary'));
  const { w, h } = calcDimensions(width, height, 111, 24);

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={w}
      height={h}
      viewBox="0 0 111 24"
      fill={fill}
      aria-label="Opendoor logo"
    >
      <g>
        <path d="M11.6113 10.5333C11.6113 4.86933 10.6554 2.52144 8.02762 2.52144C5.70996 2.52144 4.53928 4.16738 4.53928 9.87991C4.53928 15.5195 5.44718 17.8434 8.12311 17.8434C10.4883 17.8434 11.6113 16.2215 11.6113 10.5333ZM0 10.3882C0 4.02208 4.32425 1.50488 8.17104 1.50488C12.2803 1.50488 16.1506 3.99808 16.1506 10.0008C16.1506 16.4151 11.8263 18.86 7.97951 18.86C3.87048 18.86 0 16.1975 0 10.3882Z" />
        <path d="M23.6204 17.8918C22.808 17.8918 22.163 17.5287 21.7328 17.0204V8.20967C22.1151 8.06455 22.7125 7.77395 23.6681 7.77395C25.4839 7.77395 26.5113 8.98417 26.5113 12.4456C26.5113 16.8753 25.5078 17.8918 23.6204 17.8918ZM25.7467 5.66828C24.0741 5.66828 22.7365 6.46698 21.7328 7.33841H21.6134L21.6595 5.89005C21.6637 5.75814 21.5513 5.65303 21.4221 5.66791L18.1067 6.04763C18.0083 6.05898 17.9341 6.14326 17.9341 6.24354V22.3032V23.7703C17.9341 23.8877 18.0349 23.9793 18.1502 23.9661L21.5603 23.5757C21.6586 23.5646 21.7328 23.4803 21.7328 23.38V22.3032V20.0946C21.7328 19.2476 21.7091 18.6182 21.7091 18.6182C22.2346 18.7633 23.0232 18.86 23.5249 18.86C27.5147 18.86 30.4294 16.6574 30.4294 11.6227C30.4294 7.19311 28.0164 5.66828 25.7467 5.66828Z" />
        <path d="M35.6088 10.7995L39.6941 10.5816C39.6941 7.65289 39.3122 6.58778 37.8785 6.58778C36.6599 6.58778 35.7043 7.74963 35.6088 10.7995ZM31.834 12.2276C31.834 8.01586 34.9158 5.66797 38.0695 5.66797C41.3281 5.66797 43.3731 7.29903 43.4895 11.5664C43.4919 11.6493 43.4252 11.7193 43.343 11.7193H35.7588C35.6766 11.7193 35.6101 11.7892 35.6121 11.8724C35.719 15.7172 37.1936 16.7782 39.6225 16.7782C40.8081 16.7782 41.7639 16.4992 42.5638 16.1397C42.6227 16.1131 42.6911 16.1288 42.7331 16.1786L43.0508 16.5572C43.0987 16.6143 43.0982 16.6988 43.0475 16.7531C42.176 17.6843 40.4017 18.8599 38.0695 18.8599C33.9124 18.8599 31.834 16.3425 31.834 12.2276Z" />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M53.3112 5.6678C51.5673 5.6678 49.9904 6.46668 48.7242 7.3863H48.6285L48.6755 5.86017C48.6792 5.74314 48.5797 5.65031 48.4651 5.66333L45.1218 6.04733C45.0236 6.05849 44.9492 6.14258 44.9492 6.24286V18.3773C44.9492 18.4834 45.0341 18.5691 45.1385 18.5691H48.5586C48.6631 18.5691 48.7479 18.4834 48.7479 18.3773V8.23355C49.2735 8.11243 50.1336 7.91895 50.9701 7.91895C52.0928 7.91895 52.6186 8.37885 52.6186 9.85531V18.3773C52.6186 18.4834 52.7034 18.5691 52.8079 18.5691H56.2518C56.3563 18.5691 56.4413 18.4834 56.4413 18.3773V8.9115C56.4413 6.73291 55.3422 5.6678 53.3112 5.6678Z"
        />
        <path d="M66.6907 16.4153C66.3084 16.5606 65.6632 16.948 64.6837 16.948C62.8679 16.948 61.9123 15.7134 61.9123 11.9132C61.9123 7.9192 63.1069 6.63642 64.827 6.63642C65.7112 6.63642 66.2845 7.1447 66.6907 7.65297V16.4153ZM70.4894 3.29616C70.4894 2.045 70.5314 0.895427 70.5536 0.423615C70.5601 0.28557 70.4444 0.174687 70.3087 0.186966L66.8677 0.58287C66.7674 0.591987 66.6907 0.677009 66.6907 0.779148V4.1676C66.6907 5.03885 66.7384 5.93447 66.7384 5.93447C66.2367 5.78917 65.5678 5.66824 64.9228 5.66824C61.1956 5.66824 57.9939 7.67735 57.9939 12.7121C57.9939 17.1173 60.3114 18.8602 62.6768 18.8602C64.3492 18.8602 65.6871 18.1824 66.7146 17.287H66.8101L66.7616 18.623C66.7561 18.7653 66.8787 18.8778 67.0177 18.8579L70.2956 18.4871C70.4067 18.4715 70.4894 18.3751 70.4894 18.2615V3.29616Z" />
        <path d="M81.0317 12.4212C81.0317 7.53177 80.1001 6.61196 78.4753 6.61196C76.9701 6.61196 76.0862 7.72545 76.0862 11.8888C76.0862 16.851 76.8746 17.9157 78.6186 17.9157C80.0524 17.9157 81.0317 17.0687 81.0317 12.4212ZM72.0725 12.3003C72.0725 7.6527 75.3216 5.66797 78.5471 5.66797C82.0114 5.66797 85.0456 7.77382 85.0456 12.1792C85.0456 16.8266 81.8441 18.8601 78.5471 18.8601C75.0828 18.8601 72.0725 16.7297 72.0725 12.3003Z" />
        <path d="M95.2819 12.4212C95.2819 7.53177 94.3503 6.61196 92.7255 6.61196C91.2202 6.61196 90.3364 7.72545 90.3364 11.8888C90.3364 16.851 91.1249 17.9157 92.8689 17.9157C94.3026 17.9157 95.2819 17.0687 95.2819 12.4212ZM86.3225 12.3003C86.3225 7.6527 89.5718 5.66797 92.7971 5.66797C96.2614 5.66797 99.2956 7.77382 99.2956 12.1792C99.2956 16.8266 96.0943 18.8601 92.7971 18.8601C89.333 18.8601 86.3225 16.7297 86.3225 12.3003Z" />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M108.175 5.66797C106.789 5.66797 105.427 6.73308 104.687 7.53177H104.591L104.638 5.90518C104.642 5.76676 104.524 5.65662 104.388 5.67206L101.084 6.04769C100.986 6.05885 100.912 6.14294 100.912 6.24341V18.3416C100.912 18.4673 101.012 18.5693 101.137 18.5693H104.486C104.61 18.5693 104.711 18.4673 104.711 18.3416V8.52433C105.045 8.20954 105.595 7.89475 106.024 7.89475C106.24 7.89475 106.383 7.94331 106.526 8.20954C106.98 8.95986 107.34 9.39353 108.366 9.39353C109.298 9.39353 110.131 8.64526 110.131 7.4834C110.131 6.29717 109.322 5.66797 108.175 5.66797Z"
        />
      </g>
    </svg>
  );
};

export const HomeMarkerIcon: FC<SvgBgIconProps> = ({ color, bgColor, width, height }) => {
  const colorFill = getColorValue(color, getCssVariable('od-content-primary'));
  const bgFill = getColorValue(bgColor, 'white');
  const { w, h } = calcDimensions(width, height, 75, 74);

  const filterId = useId();

  return (
    <svg width={w} height={h} viewBox="0 0 69 74" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g filter={`url(#${filterId})`}>
        <path
          d="M34.5 67.3715L51.8242 49.2788C61.3919 39.2866 61.3919 23.0855 51.8242 13.0933C42.2564 3.10107 26.7436 3.10107 17.1758 13.0933C7.60806 23.0855 7.60806 39.2866 17.1758 49.2788L34.5 67.3715Z"
          fill={colorFill}
        />
        <path
          d="M35.4188 17.9798L46.0014 25.8198C46.471 26.1694 46.75 26.7208 46.75 27.299V40.3298C46.75 41.1636 46.0626 41.8427 45.2188 41.8427H23.7812C22.9374 41.8427 22.25 41.1636 22.25 40.3298V27.299C22.25 26.7141 22.529 26.1694 22.9986 25.8198L33.5812 17.9798C34.1257 17.5764 34.8743 17.5764 35.4188 17.9798Z"
          fill={bgFill}
        />
      </g>
      <defs>
        <filter
          id={filterId}
          x="0"
          y="-1"
          width="69"
          height="83.9673"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy="1" />
          <feGaussianBlur stdDeviation="2.5" />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 0.113725 0 0 0 0 0.278431 0 0 0 0 0.482353 0 0 0 0.12 0"
          />
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_8708_43064" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy="5" />
          <feGaussianBlur stdDeviation="5" />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 0.113725 0 0 0 0 0.278431 0 0 0 0 0.482353 0 0 0 0.04 0"
          />
          <feBlend
            mode="normal"
            in2="effect1_dropShadow_8708_43064"
            result="effect2_dropShadow_8708_43064"
          />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="effect2_dropShadow_8708_43064"
            result="shape"
          />
        </filter>
      </defs>
    </svg>
  );
};

export const OpendoorMobileNavLogo: FC<SvgIconProps> = ({ width, height, color }) => {
  const fill = getColorValue(color, getCssVariable('od-content-primary'));
  const { w, h } = calcDimensions(width, height, 20, 28);

  return (
    <svg
      width={w}
      height={h}
      viewBox="0 0 20 28"
      fill={fill}
      xmlns="http://www.w3.org/2000/svg"
      aria-label="Opendoor logo"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M14.1692 10.8153C14.1692 4.0304 13.0028 1.21793 9.79595 1.21793C6.96786 1.21793 5.53928 3.1895 5.53928 10.0326C5.53928 16.7885 6.6472 19.5721 9.91252 19.5721C12.7988 19.5721 14.1692 17.6293 14.1692 10.8153ZM0 10.6415C0 3.01555 5.27697 0 9.97086 0C14.9854 0 19.7084 2.98664 19.7084 10.1775C19.7084 17.8613 14.4315 20.79 9.7375 20.79C4.72306 20.79 0 17.6005 0 10.6415ZM18.6792 26.9249C18.2007 26.2937 16.3576 23.8619 16.3576 23.8619C16.1847 23.6337 15.8883 23.496 15.5703 23.496H4.13808C3.82007 23.496 3.52374 23.6337 3.35081 23.8619L1.02951 26.9245C0.955873 27.0216 0.916796 27.1347 0.916796 27.2503V27.7178C0.916796 27.8737 1.04382 28 1.20057 28H18.5078C18.6646 28 18.7916 27.8737 18.7916 27.7178V27.2503C18.7916 27.1347 18.7529 27.0219 18.6792 26.9249Z"
      />
    </svg>
  );
};

export const EqualHousingOpportunityIcon: FC<SvgIconProps> = ({ color, width, height }) => {
  const fill = getColorValue(color, 'currentColor');
  const { w, h } = calcDimensions(width, height, 36, 35);

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={w}
      height={h}
      viewBox="0 0 35 34"
      fill={fill}
      aria-label="Equal Housing Opportunity"
    >
      <g>
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M5.16943 26.2033H3.91725V26.699H5.0673V27.1175H3.91725V27.7266H5.22499V28.1455H3.43429V25.7858H5.16943V26.2033ZM6.75287 27.5038L6.98959 27.7268C6.90725 27.769 6.81592 27.7906 6.72342 27.7898C6.45707 27.7898 6.08224 27.6257 6.08224 26.9653C6.08224 26.3049 6.45707 26.1409 6.72342 26.1409C6.98959 26.1409 7.36406 26.3049 7.36406 26.9653C7.36406 27.1865 7.32137 27.3498 7.25576 27.4721L7.00599 27.2389L6.75287 27.5038ZM7.86712 28.0489L7.61012 27.807C7.75476 27.6227 7.85707 27.3534 7.85707 26.9655C7.85707 25.8583 7.03545 25.7239 6.72324 25.7239C6.41121 25.7239 5.58994 25.8583 5.58994 26.9655C5.58994 28.0734 6.41121 28.2074 6.72324 28.2074C6.86082 28.2074 7.10424 28.1817 7.32772 28.049L7.60748 28.3161L7.86712 28.0489ZM10.1771 27.3206C10.1771 27.9407 9.80193 28.2073 9.20715 28.2073C8.99054 28.2073 8.66846 28.1549 8.4582 27.9217C8.33014 27.7781 8.28075 27.5858 8.2744 27.3498V25.7858H8.77711V27.3165C8.77711 27.6462 8.96761 27.7899 9.19727 27.7899C9.53611 27.7899 9.6737 27.6259 9.6737 27.34V25.7858H10.1773V27.3206H10.1771ZM11.4974 26.3183H11.5046L11.7965 27.2516H11.1943L11.4974 26.3183ZM11.0628 27.6578H11.9339L12.0853 28.1457H12.621L11.7902 25.7858H11.2213L10.3801 28.1457H10.8992L11.0628 27.6578ZM13.4983 27.7185H14.6713V28.1455H13.0048V25.7858H13.4983V27.7185ZM16.5403 27.0865V28.1455H16.0482V25.7858H16.5403V26.6794H17.4607V25.7858H17.953V28.1455H17.4607V27.0865H16.5403ZM18.8925 26.9653C18.8925 26.3049 19.2677 26.1409 19.5329 26.1409C19.7996 26.1409 20.1745 26.3049 20.1745 26.9653C20.1745 27.6257 19.7996 27.7898 19.5329 27.7898C19.2677 27.7898 18.8925 27.6257 18.8925 26.9653ZM18.4002 26.9653C18.4002 28.0732 19.2222 28.2073 19.5329 28.2073C19.8464 28.2073 20.6675 28.0732 20.6675 26.9653C20.6675 25.8581 19.8466 25.7237 19.5329 25.7237C19.2223 25.7237 18.4002 25.8581 18.4002 26.9653ZM23.0205 27.3206C23.0205 27.9407 22.6453 28.2073 22.0504 28.2073C21.8334 28.2073 21.5122 28.1549 21.3011 27.9217C21.1732 27.7781 21.1236 27.5858 21.1178 27.3498V25.7858H21.6198V27.3165C21.6198 27.6462 21.811 27.7899 22.0415 27.7899C22.3799 27.7899 22.5176 27.6259 22.5176 27.34V25.7858H23.0207V27.3206H23.0205ZM23.9224 27.4186C23.9268 27.5499 23.9929 27.8 24.423 27.8C24.6558 27.8 24.916 27.7442 24.916 27.4938C24.916 27.3105 24.7385 27.2606 24.4891 27.2017L24.2358 27.1424C23.8534 27.0539 23.4858 26.9695 23.4858 26.4503C23.4858 26.1871 23.6282 25.7237 24.3933 25.7237C25.1169 25.7237 25.3104 26.1964 25.3139 26.4857H24.8401C24.8274 26.3813 24.7877 26.1308 24.3572 26.1308C24.1704 26.1308 23.9469 26.1998 23.9469 26.4134C23.9469 26.5984 24.0981 26.6364 24.1956 26.6595L24.7713 26.8007C25.0934 26.8799 25.3887 27.0115 25.3887 27.435C25.3887 28.1457 24.6662 28.2074 24.459 28.2074C23.5987 28.2074 23.4509 27.7111 23.4509 27.4188H23.9224V27.4186ZM26.2855 28.1455H25.794V25.7858H26.2855V28.1455ZM28.2547 25.7858H28.7142V28.1455H28.2219L27.2597 26.4623H27.2514V28.1455H26.7919V25.7858H27.311L28.2464 27.429H28.2547V25.7858ZM30.3638 26.8736H31.3487V28.1455H31.0208L30.9716 27.8499C30.8469 27.9931 30.6661 28.2071 30.2353 28.2071C29.6672 28.2071 29.1518 27.7998 29.1518 26.972C29.1518 26.3286 29.51 25.72 30.3041 25.7235C31.0282 25.7235 31.3143 26.1927 31.339 26.5194H30.8467C30.8467 26.4272 30.6786 26.1308 30.3309 26.1308C29.9785 26.1308 29.6536 26.3737 29.6536 26.978C29.6536 27.6225 30.0053 27.7898 30.3403 27.7898C30.4488 27.7898 30.8112 27.7478 30.9121 27.2706H30.3638V26.8736ZM3.82235 30.5499C3.82235 29.8895 4.19682 29.7247 4.46264 29.7247C4.72881 29.7247 5.10311 29.8895 5.10311 30.5499C5.10311 31.2099 4.72881 31.3754 4.46264 31.3754C4.19682 31.3754 3.82235 31.2099 3.82235 30.5499ZM3.32952 30.5499C3.32952 31.6567 4.15079 31.7933 4.46282 31.7933C4.77503 31.7933 5.59629 31.6569 5.59629 30.5499C5.59629 29.4429 4.77485 29.3083 4.46282 29.3083C4.15079 29.3083 3.32952 29.4431 3.32952 30.5499ZM6.94672 30.4707V29.7768H7.33778C7.64311 29.7768 7.77152 29.8729 7.77152 30.1031C7.77152 30.2082 7.77152 30.4707 7.40322 30.4707H6.94672ZM6.94672 30.8789H7.51152C8.14599 30.8789 8.26382 30.3393 8.26382 30.126C8.26382 29.663 7.98848 29.3702 7.53128 29.3702H6.45407V31.7301H6.94672V30.8789ZM9.60138 30.4707V29.7768H9.99172C10.2974 29.7768 10.4256 29.8729 10.4256 30.1031C10.4256 30.2082 10.4256 30.4707 10.0582 30.4707H9.60138ZM9.60138 30.8789H10.1665C10.8005 30.8789 10.9192 30.3393 10.9192 30.126C10.9192 29.663 10.6435 29.3702 10.1866 29.3702H9.10837V31.7301H9.60138V30.8789ZM12.1516 30.5499C12.1516 29.8895 12.5252 29.7247 12.7924 29.7247C13.0574 29.7247 13.4327 29.8895 13.4327 30.5499C13.4327 31.2099 13.0575 31.3754 12.7924 31.3754C12.5252 31.3754 12.1516 31.2099 12.1516 30.5499ZM11.6586 30.5499C11.6586 31.6567 12.4797 31.7933 12.7924 31.7933C13.1038 31.7933 13.925 31.6569 13.925 30.5499C13.925 29.4429 13.1038 29.3083 12.7924 29.3083C12.4797 29.3083 11.6586 29.4431 11.6586 30.5499ZM15.2666 30.4086V29.7768H15.8803C16.1696 29.7768 16.225 29.9618 16.225 30.0865C16.225 30.3201 16.1006 30.4086 15.8441 30.4086H15.2666ZM14.7823 31.7303H15.2666V30.8067H15.775C16.1401 30.8067 16.1602 30.9311 16.1602 31.2532C16.1602 31.4957 16.1787 31.6172 16.2117 31.7303H16.7576V31.6666C16.6525 31.6276 16.6525 31.5419 16.6525 31.1981C16.6525 30.7564 16.5474 30.6848 16.3537 30.5991C16.5869 30.521 16.7178 30.2969 16.7178 30.0378C16.7178 29.8336 16.6035 29.3706 15.9757 29.3706H14.7821V31.7303H14.7823ZM18.5542 31.7303H18.0615V29.7879H17.3489V29.3704H19.2677V29.7879H18.5542V31.7303ZM21.9126 30.9044C21.9126 31.5253 21.5381 31.7933 20.9428 31.7933C20.7271 31.7933 20.405 31.7384 20.1942 31.5056C20.066 31.3616 20.0168 31.1708 20.011 30.9344V29.3704H20.513V30.9011C20.513 31.2297 20.7038 31.3754 20.9337 31.3754C21.272 31.3754 21.4097 31.2099 21.4097 30.9244V29.3702H21.9128V30.9044H21.9126ZM24.2976 29.3704H24.7585V31.7303H24.2646L23.3017 30.047H23.2964V31.7303H22.8355V29.3704H23.3546L24.2917 31.0129H24.2976V29.3704ZM26.1489 31.7303H25.6545V29.3704H26.1489V31.7303ZM28.073 31.7303H27.5803V29.7879H26.8686V29.3704H28.7865V29.7879H28.073V31.7303ZM30.6497 31.7303H30.156V30.8393L29.3419 29.3704H29.9231L30.4126 30.3959L30.8804 29.3704H31.4381L30.6497 30.8452V31.7303ZM17.3814 1.20673L1.49561 9.03081V12.706H3.2736V23.7307H31.3695V12.706H33.5045V9.03081L17.3814 1.20673ZM27.9321 20.4104H6.7116V10.3348L17.3814 4.88196L27.9322 10.3348V20.4104H27.9321Z"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M22.2413 13.8906H12.4028V10.335H22.2413V13.8906ZM22.2413 18.9904H12.4028V15.4324H22.2413V18.9904Z"
        />
      </g>
    </svg>
  );
};

export const ErrorImage: FC<Omit<SvgIconProps, 'color'>> = ({ width, height }) => {
  const { w, h } = calcDimensions(width, height, 120, 120);

  const lightRed = '#FF896F';
  const lightBlue = '#B0D5F7';
  const deepBlue = '#0D76D6';
  const dark = '#222222';

  return (
    <svg width={w} height={h} viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M62.2828 63.2756L57.0431 68.5153H54.3983V57.6367H49.1587H34.9367C33.7391 57.6367 32.5914 58.1357 31.7431 58.9342L27.252 63.4253H43.3701C44.5677 63.4253 45.7155 63.9243 46.5638 64.7227L54.2486 72.4076H69.9676L84.0897 58.2854C84.5388 57.8363 85.0878 57.6367 85.6866 57.6367H83.2913H62.2828V63.2756Z"
        fill={dark}
      />
      <path
        d="M46.3634 83.6355H48.609C49.2078 83.6355 49.7069 83.1365 49.7069 82.5377V75.801C49.7069 75.2021 49.2078 74.7031 48.609 74.7031H46.3634C45.7646 74.7031 45.2656 75.2021 45.2656 75.801V82.5377C45.2656 83.1365 45.7646 83.6355 46.3634 83.6355Z"
        fill={lightBlue}
      />
      <path
        d="M16.0741 63.4258L7.0918 72.408V106.092C7.0918 108.587 9.08786 110.583 11.5829 110.583V99.3548C11.5829 96.8598 13.579 94.8637 16.0741 94.8637C18.5691 94.8637 20.5652 96.8598 20.5652 99.3548V110.583H25.0563V72.408L16.0741 63.4258ZM18.3196 86.9793C18.3196 87.5781 17.8206 88.0771 17.2218 88.0771H14.9762C14.3774 88.0771 13.8784 87.5781 13.8784 86.9793V75.6516C13.8784 75.0528 14.3774 74.5538 14.9762 74.5538H17.2218C17.8206 74.5538 18.3196 75.0528 18.3196 75.6516V86.9793Z"
        fill={lightBlue}
      />
      <path
        d="M37.7318 42.4671C39.179 40.7205 41.3746 39.6227 43.8198 39.6227C45.8657 39.6227 47.762 40.4211 49.1592 41.7186V23.1552H44.6182C43.7699 23.1552 43.4206 22.1073 44.0693 21.6083L53.9498 14.123H46.0155C33.59 14.123 22.961 21.6582 18.4199 32.4369C19.8172 32.0377 21.2643 31.8381 22.8113 31.8381C29.7476 31.6884 35.636 36.2294 37.7318 42.4671Z"
        fill={lightBlue}
      />
      <path
        d="M37.383 83.6355H41.8741C42.4729 83.6355 42.972 83.1365 42.972 82.5377V75.801C42.972 75.2021 42.4729 74.7031 41.8741 74.7031H37.383C36.7841 74.7031 36.2852 75.2021 36.2852 75.801V82.5377C36.2852 83.1365 36.7841 83.6355 37.383 83.6355Z"
        fill={lightBlue}
      />
      <path
        d="M56.495 99.3545C54 99.3545 52.0039 101.351 52.0039 103.846V110.582H60.9862V103.846C60.9862 101.351 58.9901 99.3545 56.495 99.3545Z"
        fill={lightBlue}
      />
      <path d="M29.4473 85.8809V110.582H40.7749V97.1087L29.4473 85.8809Z" fill={lightBlue} />
      <path
        d="M30.6447 83.6355H32.8903C33.4891 83.6355 33.9881 83.1365 33.9881 82.5377V75.801C33.9881 75.2021 33.4891 74.7031 32.8903 74.7031H30.6447C30.0459 74.7031 29.5469 75.2021 29.5469 75.801V82.5377C29.5469 83.1365 30.0459 83.6355 30.6447 83.6355Z"
        fill={lightBlue}
      />
      <path
        d="M95.7669 97.0596H75.5568C74.958 97.0596 74.459 97.5586 74.459 98.1574V110.583H84.9383C89.1799 110.583 93.2219 109.685 96.9146 108.088V98.1574C96.9146 97.5586 96.4156 97.0596 95.7669 97.0596Z"
        fill={lightBlue}
      />
      <path
        d="M54.248 72.4082V85.8816L65.4759 97.1094V110.583H69.967V72.4082H54.248Z"
        fill={lightBlue}
      />
      <path
        d="M88.3805 21.5079C89.0292 22.0069 88.6799 23.0548 87.8315 23.0548H83.2905V57.5866H85.6858C86.2347 57.5866 86.8335 57.7862 87.2826 58.2354L101.405 72.3575V80.9405C103.151 80.0922 104.998 79.4934 106.994 79.194C106.994 79.1441 106.994 79.0942 106.994 78.9944C106.994 74.104 110.337 70.0121 114.878 68.8145V42.1671H98.6103H90.0272C89.2288 42.1671 88.5801 41.4186 88.7298 40.6201C89.4284 37.0272 92.6221 34.2826 96.4645 34.2826C98.9097 34.2826 101.105 35.3805 102.552 37.127C104.399 31.6878 109.09 27.5959 114.878 26.5978V13.9229H78.5L88.3805 21.5079Z"
        fill={lightBlue}
      />
      <path
        d="M31.7921 58.9343C32.6404 58.0859 33.7882 57.6368 34.9858 57.6368H49.2077V47.5068H41.7225H16.123V63.4254H27.3509L31.7921 58.9343Z"
        fill={lightBlue}
      />
      <path
        d="M54.249 85.8816V72.4082H25.0566V110.583H29.4479V85.8816H54.249ZM45.2667 75.7516C45.2667 75.1528 45.7657 74.6538 46.3645 74.6538H48.6101C49.2089 74.6538 49.708 75.1528 49.708 75.7516V82.4883C49.708 83.0871 49.2089 83.5861 48.6101 83.5861H46.3645C45.7657 83.5861 45.2667 83.0871 45.2667 82.4883V75.7516ZM36.2845 75.7516C36.2845 75.1528 36.7835 74.6538 37.3823 74.6538H41.8734C42.4722 74.6538 42.9713 75.1528 42.9713 75.7516V82.4883C42.9713 83.0871 42.4722 83.5861 41.8734 83.5861H37.3823C36.7835 83.5861 36.2845 83.0871 36.2845 82.4883V75.7516ZM29.5478 75.7516C29.5478 75.1528 30.0468 74.6538 30.6456 74.6538H32.8911C33.49 74.6538 33.989 75.1528 33.989 75.7516V82.4883C33.989 83.0871 33.49 83.5861 32.8911 83.5861H30.6456C30.0468 83.5861 29.5478 83.0871 29.5478 82.4883V75.7516Z"
        fill="neutrals0"
      />
      <path
        d="M40.7754 97.1094V110.583H52.0032V103.846C52.0032 101.351 53.9993 99.3549 56.4944 99.3549C58.9894 99.3549 60.9855 101.351 60.9855 103.846V110.583H65.4766V97.1094H40.7754Z"
        fill="neutrals0"
      />
      <path
        d="M101.405 72.4076L87.2826 58.2854C86.8335 57.8363 86.2846 57.6367 85.6858 57.6367C85.0869 57.6367 84.538 57.8363 84.0889 58.2854L69.9668 72.4076V110.582H74.4579V98.1567C74.4579 97.5579 74.9569 97.0589 75.5557 97.0589H95.7658C96.3647 97.0589 96.8637 97.5579 96.8637 98.1567V108.087C98.4107 107.389 99.9576 106.59 101.355 105.642V80.9906V72.4076H101.405ZM87.9313 82.4877C87.9313 83.0865 87.4323 83.5855 86.8335 83.5855H84.5879C83.9891 83.5855 83.4901 83.0865 83.4901 82.4877V71.2598C83.4901 70.661 83.9891 70.162 84.5879 70.162H86.8335C87.4323 70.162 87.9313 70.661 87.9313 71.2598V82.4877Z"
        fill="neutrals0"
      />
      <path
        d="M102.553 37.1776C101.106 35.4311 98.9099 34.3333 96.4647 34.3333C92.6223 34.3333 89.4286 37.0778 88.73 40.6707C88.5803 41.4692 89.1791 42.2177 90.0274 42.2177H98.6105H114.878V26.6484C109.09 27.6465 104.349 31.7384 102.553 37.1776Z"
        fill="neutrals0"
      />
      <path
        d="M49.1588 47.5072V41.7186C47.7615 40.4211 45.8653 39.6227 43.8193 39.6227C41.3742 39.6227 39.1785 40.7206 37.7314 42.4671C35.6355 36.2294 29.7471 31.7383 22.7609 31.7383C21.214 31.7383 19.7668 31.9379 18.3696 32.3371C12.2317 34.1336 7.64079 39.5229 7.04197 46.06C6.99207 46.8085 7.59087 47.5072 8.3394 47.5072H16.0242H41.6237H49.1588Z"
        fill={deepBlue}
      />
      <path
        d="M61.0145 10.5683C62.6095 8.33109 65.9334 8.33109 67.5285 10.5683L94.5362 48.45C96.424 51.0978 94.5311 54.7721 91.2792 54.7721H37.2637C34.0119 54.7721 32.119 51.0978 34.0067 48.45L61.0145 10.5683Z"
        fill={lightRed}
      />
      <rect x="61" y="18" width="6" height="22" rx="3" fill="neutrals0" />
      <circle cx="64" cy="46" r="3" fill="neutrals0" />
      <path d="M62.2826 63.2763V57.5875V52.7471H57.043V68.5159L62.2826 63.2763Z" fill={lightBlue} />
      <path
        d="M106.995 79.0451C106.995 79.095 106.995 79.1449 106.995 79.2447C104.999 79.5442 103.103 80.143 101.406 80.9913V105.643C107.295 101.75 111.736 95.812 113.732 88.8258C114.48 86.2309 114.88 83.4864 114.88 80.642V68.8652C110.339 70.013 106.995 74.1548 106.995 79.0451Z"
        fill={deepBlue}
      />
      <path d="M57.0432 68.5159V52.7471H54.3984V57.5875V68.5159H57.0432Z" fill={deepBlue} />
      <path
        d="M54.248 72.408L46.5631 64.7232C45.7148 63.8749 44.567 63.4258 43.3694 63.4258H27.2513H16.0234L25.0057 72.408H54.248Z"
        fill={deepBlue}
      />
      <path d="M40.7749 97.1087H65.4762L54.2483 85.8809H29.4473L40.7749 97.1087Z" fill={deepBlue} />
      <path
        d="M16.0751 94.8633C13.58 94.8633 11.584 96.8593 11.584 99.3544V110.582H20.5662V99.3544C20.5662 96.8593 18.5702 94.8633 16.0751 94.8633Z"
        fill={deepBlue}
      />
      <path
        d="M86.7848 70.1621H84.5392C83.9404 70.1621 83.4414 70.6611 83.4414 71.2599V82.4878C83.4414 83.0866 83.9404 83.5856 84.5392 83.5856H86.7848C87.3836 83.5856 87.8826 83.0866 87.8826 82.4878V71.2599C87.9325 70.6611 87.4335 70.1621 86.7848 70.1621Z"
        fill={deepBlue}
      />
      <path
        d="M17.1715 74.5537H14.9259C14.3271 74.5537 13.8281 75.0527 13.8281 75.6515V86.9792C13.8281 87.578 14.3271 88.077 14.9259 88.077H17.1715C17.7703 88.077 18.2694 87.578 18.2694 86.9792V75.6515C18.3193 75.0527 17.8202 74.5537 17.1715 74.5537Z"
        fill={deepBlue}
      />
    </svg>
  );
};
